/**
 * EPIC-DM01: Intake Form Component
 * Captures prospect information, legal consents, and optional social handles
 * 
 * Design Reference: Figma Intake (iPad + phone) - booth-friendly single screen
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react';
import { TERMS_VERSION, WAIVER_VERSION, type IntakePayload } from '@/hooks/useDemoMode';

interface IntakeFormProps {
  sessionId: string;
  onSubmit: (data: IntakePayload) => Promise<void>;
  isSubmitting: boolean;
  error?: Error | null;
}

interface FormState {
  name: string;
  email: string;
  handicap: string;
  acceptedTerms: boolean;
  acceptedWaiver: boolean;
  allowSocialSharing: boolean;
  instagram: string;
  x: string;
  tiktok: string;
  facebook: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  acceptedTerms?: string;
  acceptedWaiver?: string;
}

export function IntakeForm({ sessionId, onSubmit, isSubmitting, error }: IntakeFormProps) {
  const [formState, setFormState] = useState<FormState>({
    name: '',
    email: '',
    handicap: '',
    acceptedTerms: false,
    acceptedWaiver: false,
    allowSocialSharing: false,
    instagram: '',
    x: '',
    tiktok: '',
    facebook: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [optionalOpen, setOptionalOpen] = useState(false);

  // Validate form
  const validate = (): boolean => {
    const errors: FormErrors = {};

    if (!formState.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formState.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formState.acceptedTerms) {
      errors.acceptedTerms = 'You must accept the Terms of Service';
    }

    if (!formState.acceptedWaiver) {
      errors.acceptedWaiver = 'You must accept the Liability Waiver';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const socialHandles: IntakePayload['social_handles'] = {};
    if (formState.instagram.trim()) socialHandles.instagram = formState.instagram.trim();
    if (formState.x.trim()) socialHandles.x = formState.x.trim();
    if (formState.tiktok.trim()) socialHandles.tiktok = formState.tiktok.trim();
    if (formState.facebook.trim()) socialHandles.facebook = formState.facebook.trim();

    const payload: IntakePayload = {
      demo_session_id: sessionId,
      name: formState.name.trim(),
      email: formState.email.trim(),
      handicap: formState.handicap ? parseInt(formState.handicap, 10) : undefined,
      accepted_terms: formState.acceptedTerms,
      terms_version: TERMS_VERSION,
      accepted_waiver: formState.acceptedWaiver,
      waiver_version: WAIVER_VERSION,
      allow_social_sharing: formState.allowSocialSharing,
      social_handles: Object.keys(socialHandles).length > 0 ? socialHandles : undefined,
    };

    await onSubmit(payload);
  };

  // Update form field
  const updateField = (field: keyof FormState, value: string | boolean) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 text-destructive rounded-md p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Error submitting form</p>
            <p className="text-sm text-destructive/80">{error.message}</p>
          </div>
        </div>
      )}

      {/* Required Fields Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Enter your full name"
              value={formState.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={formErrors.name ? 'border-destructive' : ''}
              autoComplete="name"
            />
            {formErrors.name && (
              <p className="text-sm text-destructive">{formErrors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={formState.email}
              onChange={(e) => updateField('email', e.target.value)}
              className={formErrors.email ? 'border-destructive' : ''}
              autoComplete="email"
            />
            {formErrors.email && (
              <p className="text-sm text-destructive">{formErrors.email}</p>
            )}
          </div>

          {/* Handicap (Optional in this card) */}
          <div className="space-y-2">
            <Label htmlFor="handicap">
              Golf Handicap <span className="text-muted-foreground text-sm">(optional)</span>
            </Label>
            <Input
              id="handicap"
              type="number"
              placeholder="e.g., 15"
              min="0"
              max="54"
              value={formState.handicap}
              onChange={(e) => updateField('handicap', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Legal Consents Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Legal Agreements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Terms of Service */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="terms"
              checked={formState.acceptedTerms}
              onCheckedChange={(checked) => updateField('acceptedTerms', checked === true)}
              className={formErrors.acceptedTerms ? 'border-destructive' : ''}
            />
            <div className="space-y-1 leading-none">
              <Label 
                htmlFor="terms" 
                className={`cursor-pointer ${formErrors.acceptedTerms ? 'text-destructive' : ''}`}
              >
                I accept the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary underline hover:no-underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>
                <span className="text-destructive"> *</span>
              </Label>
              {formErrors.acceptedTerms && (
                <p className="text-sm text-destructive">{formErrors.acceptedTerms}</p>
              )}
            </div>
          </div>

          {/* Liability Waiver */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="waiver"
              checked={formState.acceptedWaiver}
              onCheckedChange={(checked) => updateField('acceptedWaiver', checked === true)}
              className={formErrors.acceptedWaiver ? 'border-destructive' : ''}
            />
            <div className="space-y-1 leading-none">
              <Label 
                htmlFor="waiver" 
                className={`cursor-pointer ${formErrors.acceptedWaiver ? 'text-destructive' : ''}`}
              >
                I accept the{' '}
                <a 
                  href="/waiver" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary underline hover:no-underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Liability Waiver
                </a>
                <span className="text-destructive"> *</span>
              </Label>
              {formErrors.acceptedWaiver && (
                <p className="text-sm text-destructive">{formErrors.acceptedWaiver}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Optional Social Section (Collapsed by Default) */}
      <Collapsible open={optionalOpen} onOpenChange={setOptionalOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Social Media & Sharing
                  <span className="text-sm font-normal text-muted-foreground ml-2">(optional)</span>
                </CardTitle>
                {optionalOpen ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              {/* Social Sharing Consent */}
              <div className="flex items-start space-x-3 bg-muted/50 p-3 rounded-md">
                <Checkbox
                  id="social-sharing"
                  checked={formState.allowSocialSharing}
                  onCheckedChange={(checked) => updateField('allowSocialSharing', checked === true)}
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="social-sharing" className="cursor-pointer">
                    I consent to my swing analysis being shared on social media
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    We may feature your swing on our social channels. You can withdraw consent at any time.
                  </p>
                </div>
              </div>

              {/* Social Handles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    placeholder="@username"
                    value={formState.instagram}
                    onChange={(e) => updateField('instagram', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="x">X (Twitter)</Label>
                  <Input
                    id="x"
                    placeholder="@username"
                    value={formState.x}
                    onChange={(e) => updateField('x', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tiktok">TikTok</Label>
                  <Input
                    id="tiktok"
                    placeholder="@username"
                    value={formState.tiktok}
                    onChange={(e) => updateField('tiktok', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="facebook">Facebook</Label>
                  <Input
                    id="facebook"
                    placeholder="Profile URL or username"
                    value={formState.facebook}
                    onChange={(e) => updateField('facebook', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Submit Button */}
      <Button
        type="submit"
        size="lg"
        className="w-full h-14 text-lg"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Submitting...
          </>
        ) : (
          'Continue to Record Swing'
        )}
      </Button>
    </form>
  );
}
